import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const StarRating = ({ rating, size = 'w-4 h-4', color = '#FBBF24', emptyColor, starColor }) => (
    <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
            <svg key={i} className={size} viewBox="0 0 20 20" fill={i <= rating ? (starColor || color) : (emptyColor || '#E5E7EB')}>
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
        ))}
    </div>
);

const SOURCE_ICONS = {
    'Google': '🔍',
    'TripAdvisor': '🦉',
    'Facebook': '📘',
    'Yelp': '⭐',
    'Booking': '🏨',
};

export default function ReviewsSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'cards';
    const average = content.average || '4.8';
    const total = content.total || '250';
    const source = content.source || 'Google';

    if (items.length === 0) return null;

    const revColumns = config.columns || 3;
    const revColClass = revColumns === 2 ? 'md:grid-cols-2' : revColumns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Summary bar ──
    const SummaryBar = () => (
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 p-5 rounded-2xl transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }}>
            <div className="text-center sm:text-left">
                <div className="text-4xl font-bold" style={{ color: s.accent }}>{average}</div>
                <StarRating rating={Math.round(parseFloat(average))} size="w-5 h-5" color={s.accent} emptyColor={s.dark ? 'rgba(255,255,255,0.15)' : '#E5E7EB'} starColor={config.star_color} />
            </div>
            <div className="text-sm" style={{ color: s.textSub }}>
                <span className="font-semibold" style={{ color: s.text }}>{total} {content.reviews_label || 'resenas'}</span>
                <span className="flex items-center gap-1.5 mt-0.5">
                    {SOURCE_ICONS[source] || '⭐'} {source}
                </span>
            </div>
        </div>
    );

    // ── Compact variant ──
    if (variant === 'compact') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Resenas'} subtitle={content.subtitle} config={config} s={s} />
                    <SummaryBar />
                    <div className="space-y-3 max-w-2xl mx-auto">
                        {items.map((item, i) => (
                            <div key={i} className={`flex items-start gap-3 py-3 transition-all duration-300 ${i > 0 ? 'border-t' : ''} ${visible ? 'opacity-100' : 'opacity-0'}`}
                                style={{ transitionDelay: visible ? `${300 + i * 80}ms` : '0ms', ...(i > 0 ? { borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' } : {}) }}>
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: s.accent }}>
                                    {item.name?.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold" style={{ color: s.text }}>{item.name}</span>
                                        <StarRating rating={item.rating || 5} size="w-3 h-3" color={s.accent} emptyColor={s.dark ? 'rgba(255,255,255,0.15)' : '#E5E7EB'} starColor={config.star_color} />
                                    </div>
                                    <p className="text-sm mt-0.5" style={{ color: s.textSub }}>{item.text}</p>
                                    <span className="text-[10px] mt-1 block" style={{ color: s.textSub }}>{item.date} {item.source ? `· ${item.source}` : ''}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Resenas'} subtitle={content.subtitle} config={config} s={s} />
                <SummaryBar />
                <div className={`grid grid-cols-1 ${revColClass} gap-6`}>
                    {items.map((item, i) => (
                        <div key={i} className={`${card} p-5 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${300 + i * 120}ms` : '0ms' }}>
                            <StarRating rating={item.rating || 5} color={s.accent} emptyColor={s.dark ? 'rgba(255,255,255,0.15)' : '#E5E7EB'} starColor={config.star_color} />
                            <p className="text-sm mt-3 leading-relaxed" style={{ color: s.textSub }}>"{item.text}"</p>
                            <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: s.accent }}>
                                        {item.name?.charAt(0)}
                                    </div>
                                    <span className="text-sm font-medium" style={{ color: s.text }}>{item.name}</span>
                                </div>
                                <span className="text-[10px]" style={{ color: s.textSub }}>{item.source || source}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
